// live-snapshot.ts — Cut 4.2: build a plain-text snapshot of the current
// live numbers and ship it alongside every /ask query.
//
// Why client-side: the Metabase API key is bound on the desktop side (via
// the Tauri Rust backend), not in the Worker. Re-plumbing Metabase auth
// into the Worker is a bigger move; for now we pull the snapshot on the
// machine that already has the key and send the formatted text as part of
// the /ask payload. The Worker drops it into the system prompt as the
// "## Live numbers" block.
//
// The snapshot stays small (top-N per query, one line each) so it doesn't
// crowd the Claude context window — the model needs a quick sense of where
// the numbers stand, not the full warehouse.

import { runCatalogQuery, type QueryRunResult } from "./query-catalog";

/** Best-effort snapshot. If any query fails or hasn't been bound, we just
 * skip that section — the Worker handles a partial or empty snapshot
 * cleanly. Returns "" if absolutely nothing is available. */
export async function buildLiveSnapshot(): Promise<string> {
  // The three queries that already power the supply/demand surfaces.
  // Each returns rows where col 0 is a category code and col 1 is the
  // value. The "mode" tells the formatter whether the value is already a
  // percentage (retention, activation %) or a raw count that we need to
  // normalise into a share. The spend-share query returns event counts
  // (e.g. 22,429 for fee-defense), so naively appending "%" produced
  // nonsense like "22,429%" — we now sum the column and divide each row by
  // the total to get the actual share of redeem volume.
  const ids = [
    {
      id: "live-spend-share-by-category",
      heading: "Spend share by category (last 30d, % of redeem volume)",
      mode: "share-from-counts" as const,
    },
    {
      id: "live-retention-14d-by-category",
      heading: "14-day retention by category (% of redeemers returning)",
      mode: "as-percentage" as const,
    },
    {
      id: "live-first-redeem-active-share",
      heading: "First-redeem active share (% of issued cohort that redeemed)",
      mode: "as-percentage" as const,
    },
  ] as const;

  const sections: string[] = [];
  // Fire all queries in parallel; if one errors, the others still surface.
  const results = await Promise.allSettled(
    ids.map(({ id }) => runCatalogQuery(id)),
  );

  for (let i = 0; i < ids.length; i++) {
    const meta = ids[i]!;
    const settled = results[i]!;
    if (settled.status !== "fulfilled") continue;
    const res = settled.value;
    const block = formatQueryBlock(meta.heading, meta.mode, res);
    if (block) sections.push(block);
  }

  // Snapshot timestamp so the model knows how fresh the read is.
  if (sections.length > 0) {
    const now = new Date().toISOString();
    sections.unshift(`Snapshot pulled: ${now}`);
  }
  return sections.join("\n\n");
}

type SnapshotMode = "share-from-counts" | "as-percentage";

function formatQueryBlock(
  heading: string,
  mode: SnapshotMode,
  res: QueryRunResult,
): string {
  if (res.status === "error") return "";
  if (!res.rows || res.rows.length === 0) return "";
  const tag = res.status === "live" ? "LIVE" : "SAMPLE";
  const lines = [`${heading} [${tag}]`];

  // Filter to rows with a category code + finite numeric value.
  const valid = res.rows.filter(
    (r) => r[0] != null && Number.isFinite(Number(r[1])),
  );
  if (valid.length === 0) return "";

  // If we're given raw counts, compute the column sum so each row
  // becomes a true percentage of total. For already-% queries (retention,
  // activation), values pass through untouched.
  const sum =
    mode === "share-from-counts"
      ? valid.reduce((acc, r) => acc + Number(r[1]), 0)
      : 0;

  // Normalise each row to a percentage if needed.
  const rows = valid.map((r) => {
    const code = String(r[0]);
    const raw = Number(r[1]);
    const pct =
      mode === "share-from-counts" && sum > 0
        ? (raw / sum) * 100
        : raw;
    return { code, pct };
  });

  // Sort desc by pct so the chunky ones land first; cap at 12 rows.
  rows.sort((a, b) => b.pct - a.pct);
  for (const row of rows.slice(0, 12)) {
    // Format: 1 dp, trim trailing zero. Always trailing "%" because all
    // three queries are percentage-valued after normalisation.
    const num =
      row.pct >= 10
        ? row.pct.toFixed(0)
        : row.pct >= 1
          ? row.pct.toFixed(1)
          : row.pct.toFixed(2);
    lines.push(`  ${row.code.padEnd(16, " ")}${num}%`);
  }

  // For share-from-counts mode, include the raw total so the model has
  // a sense of denominator size when reasoning about the percentages.
  if (mode === "share-from-counts" && sum > 0) {
    lines.push(`  (total events: ${Math.round(sum).toLocaleString()})`);
  }

  return lines.join("\n");
}
